import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'

// Selectors
import {
  selectShowAVMDiffWarning,
  selectShowNonDisclosureStateWarning,
  selectShowLowConfidenceWarning
} from 'src/store/selectors/home/metadata'
import { selectShouldRenderAvmHotMarketWarning } from 'src/store/selectors/display'
import { hasCustomerToken } from 'src/auth'

// Actions
import { clientSnoozeAvmHotMarket } from 'src/actions/client'
import { handleAcknowledgeClientHasSeenAvmDiff } from 'src/actions/home'
import { handleAcknowledgeTuneValue } from 'src/actions/homeowner'

// Utils
import { dispatchEvent } from 'src/helpers/tagManager'
import {
  CloseAvmDifferenceToolTipClick,
  CloseNonDisclosureToolTipClick,
  CloseLowConfidenceToolTipClick,
  CloseHotMarketToolTipClick
} from 'src/constants/trackingEvents'

// Components
import AvmDifferenceToolTipWarning from './ToolTipWarnings/AvmDifferenceToolTipWarning'
import BaseChart from 'src/Components/shared/Chart'
import LowConfidenceToolTipWarning from './ToolTipWarnings/LowConfidenceToolTipWarning'
import NonDisclosureToolTipWarning from './ToolTipWarnings/NonDisclosureToolTipWarning'
import AvmHotMarketToolTipWarning from './ToolTipWarnings/AvmHotMarketToolTipWarning'
import Tooltip from './Tooltip'

// Styles
import styles from './Chart.module.scss'

const Chart = props => {
  const chartRef = useRef()
  const dispatch = useDispatch()
  const [forceHide, setForceHide] = useState(false)

  const showAvmDiffWarning = useSelector(selectShowAVMDiffWarning)
  const showNonDisclosureWarning = useSelector(
    selectShowNonDisclosureStateWarning
  )
  const showLowConfidenceWarning = useSelector(selectShowLowConfidenceWarning)

  const showAvmHotMarketWarning = useSelector(
    selectShouldRenderAvmHotMarketWarning
  )

  const renderChildren = () => {
    // ORDER DEPENDENT - DO NOT CHANGE
    if (showAvmHotMarketWarning) {
      return <AvmHotMarketToolTipWarning setForceHide={setForceHide} />
    }

    if (showNonDisclosureWarning) {
      return <NonDisclosureToolTipWarning setForceHide={setForceHide} />
    }

    if (showAvmDiffWarning) {
      return <AvmDifferenceToolTipWarning />
    }

    if (showLowConfidenceWarning) {
      return <LowConfidenceToolTipWarning setForceHide={setForceHide} />
    }
  }

  const getEvent = () => {
    // ORDER DEPENDENT - DO NOT CHANGE
    if (showAvmHotMarketWarning) {
      return CloseHotMarketToolTipClick
    }

    if (showNonDisclosureWarning) {
      return CloseNonDisclosureToolTipClick
    }

    if (showAvmDiffWarning) {
      return CloseAvmDifferenceToolTipClick
    }

    if (showLowConfidenceWarning) {
      return CloseLowConfidenceToolTipClick
    }
  }

  const getAction = () => {
    // ORDER DEPENDENT - DO NOT CHANGE
    if (showAvmHotMarketWarning) {
      return clientSnoozeAvmHotMarket
    }

    if (showNonDisclosureWarning || showLowConfidenceWarning) {
      return handleAcknowledgeTuneValue
    }

    if (showAvmDiffWarning) {
      return handleAcknowledgeClientHasSeenAvmDiff
    }
  }

  const handleClose = () => {
    setForceHide(true)
    if (hasCustomerToken()) return

    const event = getEvent()
    if (event) {
      dispatchEvent(event)
    }

    const action = getAction()
    if (action) {
      dispatch(action())
    }
  }

  const showWarning =
    showAvmHotMarketWarning ||
    showNonDisclosureWarning ||
    showAvmDiffWarning ||
    showLowConfidenceWarning
  const showToolTip = showWarning && !forceHide

  return (
    <div className={styles.chartWrapper}>
      <div
        data-qa='valuation-chart'
        className={classnames(
          styles.container,
          showToolTip && styles.obfuscate
        )}
      >
        <BaseChart {...props} className={styles.chart} ref={chartRef} />
        <Tooltip
          data-qa='tool-tip'
          show={showToolTip}
          handleClose={handleClose}
          ref={chartRef}
        >
          {renderChildren()}
        </Tooltip>
      </div>
    </div>
  )
}

export default Chart
